import { useRef, useEffect, useState, React } from 'react';
import ReactDOM from 'react-dom';
import Sketch from 'react-p5'

import { ChromaticVert, ChromaticFrag } from './abberration/ChromaticAbberration.js';
import PostProcessingStack from  './abberration/PostProcessingStack.js';
import { updateNoise, addNoiseToStack } from './abberration/NoiseEffect.js';


import './index.css';

import * as Tone from 'tone';
import { Sonification } from './sonification.js';

const EMBEDDINGS_NUM = 50257;
const BACKGROUND_COLOR = 0;
const SPLIT_LINES = true;
const MOTION_BLUR = false;
const ANIMATION_LENGTH = 5 * 60 * 1000; // ms
const ANIMATION_SPEED = 0.005;
const NOISE = false;
const FIG_DIMENSIONS = [1000, 800, 800] // width, height, depth

const FULLSCREEN_MODE = document.fullscreenEnabled ||
    document.webkitFullscreenEnabled ||
    document.mozFullScreenEnabled ||
    document.msFullscreenEnabled;

let [width, height] = FULLSCREEN_MODE ? [window.screen.width, window.screen.height] : [window.innerWidth, window.innerHeight];
const [smaller, bigger] = [Math.min(width, height), Math.max(width,height)];
const SCREEN_ORIENTATION = window.matchMedia("(orientation: portrait)").matches ? 'portrait' : 'landscape';
console.log(`width: ${width}, height: ${height}, smaller: ${smaller}, bigger: ${bigger}, orientation: ${SCREEN_ORIENTATION}`);
if (SCREEN_ORIENTATION === 'landscape') {
  width = bigger;
  height = smaller;
}
else {
  width = smaller;
  height = bigger;
}

const randomEmbeddingIdx = () => { return parseInt(Math.random()*EMBEDDINGS_NUM) };

const Kasina = ({intention, restart}) => {
  // Embedding
  const [idx] = useState(randomEmbeddingIdx());
  const [embedding, setEmbedding] = useState([]);

  // Geometry
  const [points, setPoints] = useState([]);
  const [lines, setLines] = useState([]);

  // Canvas width and height
  // const [width, setWidth] = useState(window.screen.width)
  // const [height, setHeight] = useState(window.screen.height);
  const figDimensions = useRef(FIG_DIMENSIONS.slice());
  const cameraDepth = useRef(FIG_DIMENSIONS[2]);
  // let width = window.screen.width;
  // let height = window.screen.height;

  const [canvas, setCanvas] = useState();

  // Fades and changes
  const rotationAngle = useRef(0);
  const cameraMotion = useRef(true);
  const cameraZPosition = useRef(0);

  const clearPreviousFrame = useRef(true);
  const opacity = useRef(0);
  const changeInProcess = useRef(false);

  // Effects
  const [postProcessingStack, setPostProcessingStack] = useState();

  // Sonification
  const sonification = useRef(null);

  const fetchEmbedding = () => {
    console.log(`Fetching embedding ${idx}`);
    fetch(`/embeddings/${idx}`)
      .then((res) => res.json())
      .then((data) => {
        setEmbedding(data.embedding);

        if (sonification.current===null) sonification.current = new Sonification(data.embedding);
        else sonification.current.setEmbedding(data.embedding);
        changeInProcess.current = false;
      });
  }

  const calculatePoints = () => {
    const [w, h, d] = figDimensions.current;
    const emb = embedding.embedding;
    if (emb && emb.length) {
      const normalized = emb.map(x => x);
      const newPoints = []
      for(let i = 0; i < normalized.length; i += 3) {
        let [x, y, z] = [normalized[i] * w, normalized[i+1] * h, normalized[i+2] * d];
        newPoints.push([x,y,z]);
      }
      setPoints(newPoints);
    }
  }

  const dist = (a, b) => {
    return ((b[0]-a[0])**2+(b[1]-a[1])**2+(b[2]-a[2])**2)**(1/2);
  }

  const calculateLines = () => {

    const splitLine = (a, b) => {
      const p5 = window.p5.prototype;
      const noisePos = p5.random(1000);
      const newLines = []

      const d = dist(a, b);

      // How dramatic should be the deviations from original path
      const noiseMul = d/5;

      // How many additional steps in a line
      const steps = d/10;


      let [lastX, lastY, lastZ] = a;

      const initialShiftX = p5.noise(noisePos, 0, 0)*noiseMul;
      const initialShiftY = p5.noise(noisePos, 1, 0)*noiseMul;
      const initialShiftZ = p5.noise(noisePos, 2, 0)*noiseMul;

      for (let step=0; step<steps; ++step) {
        const x = p5.lerp(a[0], b[0], step/steps) + p5.noise(noisePos, 0, step/steps)*noiseMul - initialShiftX;
        const y = p5.lerp(a[1], b[1], step/steps) + p5.noise(noisePos, 1, step/steps)*noiseMul - initialShiftY;
        const z = p5.lerp(a[2], b[2], step/steps) + p5.noise(noisePos, 2, step/steps)*noiseMul - initialShiftZ;

        const weight = p5.noise(noisePos+1, 0, step/steps)*1.5;

        newLines.push([[lastX, lastY, lastZ], [x, y, z], weight]);

        [lastX, lastY, lastZ] = [x, y, z];
      }

      newLines.push([[lastX, lastY, lastZ], b, 1]);

      return newLines;
    }
    const newLines = [];

    let relationshipDict = { };
    for (let i=0; i<points.length; ++i) {
      const point = points[i];
      let neighbour = points[0];
      let distance = 1 << 30;
      for (let j=0; j<points.length; ++j) {
        const potential = points[j];
        if (i === j) continue;
        if (relationshipDict[j] === i) continue;

        const curDist = dist(point, potential);
        if (Math.abs(curDist) < distance) {
          distance = curDist;
          neighbour = potential;
          relationshipDict[i] = j;
        }
      }

      if(SPLIT_LINES) {
        newLines.push(...splitLine(point, neighbour));
      }
      else {
        newLines.push([point, neighbour, 1]);
      }
    }

    setLines(newLines);
  }

  const updateState = (p5) => {
    // if (p5.frameCount >= ANIMATION_LENGTH) {
    //   sonification.current.stop();
    //   restart();
    // }
    // clearPreviousFrame.current = Math.floor(rotationAngle.current / (Math.PI/3)) % 2 - 1;

    // After zoom out, start filling up
    if (cameraMotion.current && cameraZPosition.current <= -cameraDepth.current) {
      cameraMotion.current = false;
      setTimeout(() => { clearPreviousFrame.current = false }, 15*1000);
    }

    if (rotationAngle.current > 2*Math.PI) {
      rotationAngle.current %= Math.PI;
    }
  }

  useEffect(fetchEmbedding, [idx]);
  useEffect(calculatePoints, [embedding]);
  useEffect(calculateLines, [points]);

  const setup = (p5, canvasParentRef) => {
    setTimeout(() => {
      sonification.current.stop();
      restart();
    }, ANIMATION_LENGTH);
    figDimensions.current = [width*0.8, height*0.8, width*0.8];
    cameraDepth.current = SCREEN_ORIENTATION === 'landscape' ? height : width * 1.4;
    p5.createCanvas(width, height).parent(canvasParentRef);

    // p5.pixelDensity(2);

    const cnv = p5.createGraphics(width, height, p5.WEBGL)
    cnv.background(BACKGROUND_COLOR);
    setCanvas(cnv);

    if (FULLSCREEN_MODE) {
      p5.fullscreen(true);
    }

    // sonification.current.startAudio();
    cnv.mousePressed(() => {
      sonification.current.startAudio();
    });

    // Abberration
    let src = [
      {
        'effect': 'chromatic',
        'vert': ChromaticVert,
        'frag': ChromaticFrag
      }
    ];
    // create a post processing stack
    let pps = new PostProcessingStack(p5, cnv, src, width, height);
    setPostProcessingStack(pps);

    // add the noise effect to the stack
    if (NOISE) {
      addNoiseToStack(pps);
    }
  }

  const draw = (p5) => {
    let prevFrame = canvas.get(0,0, width,height);

    // if (CLEAR_PREVIOUS_FRAME) {
    if (clearPreviousFrame.current) {
      canvas.clear();
      canvas.background(BACKGROUND_COLOR);
    }
    if (MOTION_BLUR) {
      canvas.tint(255, 225);
      canvas.image(prevFrame,-width/2,-height/2);
    }

    rotationAngle.current += ANIMATION_SPEED * 2;
    // canvas.ortho();
    if(cameraMotion.current) {
      cameraZPosition.current = -cameraDepth.current + p5.cos(p5.frameCount * ANIMATION_SPEED) * cameraDepth.current;
      canvas.camera(0, 0, cameraZPosition.current, 0, 0, 0, 0, 1, 0);
    }
    canvas.rotateY(rotationAngle.current);
    // canvas.rotateY(0.025);

    // Drawing points
    // let plen = points.length;
    // for (let i=0; i<plen; ++i) {
    //   canvas.push()
    //   let p = points[i];
    //   canvas.translate(p[0], p[1], p[2]);
    //   canvas.normalMaterial();
    //   canvas.box(1, 1, 1);
    //   canvas.pop();
    // }
    // End drawing points

    // Drawing lines
    let len = lines.length;
    // if (lines.length) { len = 40 }
    for (let i=0; i<len; ++i) {
      const line = lines[i];
      let [x1, y1, z1, x2, y2, z2] = [line[0][0], line[0][1], line[0][2], line[1][0], line[1][1], line[1][2]];

      const [red, green, blue] = [100, 100, 300];
      const div = 100;
      const step = p5.frameCount / 1;
      canvas.stroke(p5.noise(0, (i+step)/div)*red, p5.noise(1, (i+step)/div)*green, p5.noise(2, (i+step)/div)*blue, opacity.current);
      // canvas.stroke(()*255);
      // canvas.stroke(p5.lerpColor(p5.color(255,255,255), p5.color(255,0,0), Math.abs(z1)/200));
      // canvas.stroke(Math.abs(x1)/800*155+50, Math.abs(y1)/800*155+50, Math.abs(z1)/800*155+50);
      // p5.stroke(0, opacity.current);
      canvas.strokeWeight(line[2]*1);
      canvas.line(x1, y1, z1, x2, y2, z2);
    }
    // End drawing lines
    canvas.rotateY(-rotationAngle.current);

    // Image chromatic abberration
    if (NOISE) {
      updateNoise(p5, postProcessingStack);
    }

    const op = 0.25 + (1+p5.sin(p5.frameCount/10))/2/4;
    postProcessingStack.setUniform('chromatic', 'u_strength', 1.0);
    postProcessingStack.setUniform('chromatic', 'u_time', p5.millis() / 1000.0);
    postProcessingStack.setUniform('chromatic', 'u_op', op);
    postProcessingStack.applyEffects();
    // End Image aberration


    // Token
    // p5.translate(width/2, height/2);
    // p5.textSize(20);
    // p5.fill(0, 0, 255);
    // p5.noStroke();
    // p5.textAlign(p5.RIGHT, p5.BOTTOM);
    // p5.text(embedding.token, 0, 0, width-40, height-40);
    // End token

    // Embedding corners
    const emb = embedding.embedding ? embedding.embedding : [0,0,0,0];
    // p5.translate(width/2, height/2);
    p5.textSize(20);
    p5.fill(0,0,255);
    p5.noStroke();
    p5.textAlign(p5.LEFT, p5.TOP);
    p5.text(emb[p5.frameCount%emb.length], 40, 40, width-40, height-40);

    p5.textAlign(p5.RIGHT, p5.TOP);
    p5.text(emb[(p5.frameCount+1)%emb.length], 0, 40, width-40, height-40);

    p5.textAlign(p5.LEFT, p5.BOTTOM);
    p5.text(emb[(p5.frameCount+2)%emb.length], 40, 0, width-40, height-40);

    p5.textAlign(p5.RIGHT, p5.BOTTOM);
    p5.text(emb[(p5.frameCount+3)%emb.length], 0, 0, width-40, height-40);
    // End embeddings conrners

    // idx
    // p5.textAlign(p5.RIGHT, p5.TOP);
    // p5.text(`#${idx}, Intention: ${intention}, frameCount: ${p5.frameCount}`, 0, 40, width-40, height-40);
    // End idx

    // Embedding display
    // p5.fill(0,0,255);
    // p5.textSize(10);
    // p5.textAlign(p5.LEFT, p5.TOP);
    // const embText = embedding.embedding ? embedding.embedding.slice(p5.frameCount%2560).join("\n") : '';
    // p5.text(embText, 0, 0, 100, height + 100);
    // End embedding display

    // p5.filter(p5.INVERT);
    updateState(p5);
  }

  return (
    <div>
      <Sketch setup={setup} draw={draw} />
    </div>
  );
}

const App = () => {
  const [started, setStarted] = useState(false);
  const [intention, setIntention] = useState('');

  const handleStarted = (e) => {
    e.preventDefault();
    Tone.start();
    setStarted(true);
  }

  const intentionChanged = (e) => {
    e.preventDefault();
    const val = e.target.value;
    setIntention(val);
  }

  const restart = () => {
    setStarted(false);
  }

  if (started) return <Kasina intention={intention} restart={restart} />;
  else return (
    <div className="start-screen">
      <p>
        Input a sentence and the system will transform it into a universe of relations that holds the understanding of your words.
      </p>
      <p>
        Observe how this universe transforms into a form. This form is a Kasina representing the profound essence of your intention.
      </p>
      <p>
        Look straight into the Kasina. Practice your focus and the energy of the Kasina will help you better internalise your intention. The meditation will take around 5 minutes.
      </p>
      <p>
        What is your intention?<span className="super">*</span>
      </p>
      <div className="intention-input">
        <input type="text" name="intention" onChange={intentionChanged} />
        <button onClick={handleStarted}>Start</button>
      </div>

      <div className="super-explanation"><span className="super">*</span>No information is being stored.</div>

      <p className="quote">
        "The reality is made of tensors and this is why neural networks excel in its comprehension."
      </p>
    </div>
  );
};

ReactDOM.render(
  <App />,
  document.getElementById('root')
);
